import { useEffect, useRef } from "react";
import { useGetIdentity, useListFilterContext } from "ra-core";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

/**
 * OnlyMineToggle — switch reutilizable "Solo mis [recurso]" que se aplica
 * a cualquier lista del CRM (companies, contacts, deals, tasks, meetings...).
 *
 * Lógica:
 *   - Si el usuario es ADMIN → el toggle aparece OFF por default (ve todo);
 *     puede activarlo manualmente para filtrar solo lo suyo.
 *   - Si el usuario NO es admin → el toggle aparece ON por default y el
 *     filtro se aplica automáticamente al montar la vista. Puede apagarlo
 *     manualmente (la RLS del CRM es permisiva — se asume "tu equipo
 *     comparte data" — pero por default ven solo lo de ellos).
 *
 * El filtro se llama `filterKey` y su valor por default es `identity.id`.
 * Para meetings se usa `sales_participant_ids@cs` con valor `{<id>}` para
 * usar contains-array de PostgREST.
 *
 * Props:
 *   - filterKey: clave del filtro a setear (e.g. "sales_id" o
 *     "sales_participant_ids@cs").
 *   - filterValueBuilder: opcional. Función que toma el id del user y
 *     produce el valor del filtro (e.g. para PostgREST array: (id) => `{${id}}`).
 *     Default: (id) => id.
 *   - label: texto del switch (e.g. "Solo mis juntas").
 */
export function OnlyMineToggle({
  filterKey = "sales_id",
  filterValueBuilder,
  label = "Solo lo mío",
  className,
}: {
  filterKey?: string;
  filterValueBuilder?: (id: number | string) => unknown;
  label?: string;
  className?: string;
}) {
  const { filterValues, displayedFilters, setFilters } = useListFilterContext();
  const { identity, isPending } = useGetIdentity();
  const initializedRef = useRef(false);

  const isAdmin = (identity as any)?.administrator === true;
  const isActive = typeof filterValues?.[filterKey] !== "undefined";

  // Auto-aplicar para non-admin al primer render. Solo una vez —
  // después el usuario puede toggle libre.
  useEffect(() => {
    if (isPending || initializedRef.current) return;
    if (!identity?.id) return;
    initializedRef.current = true;
    if (!isAdmin && !isActive) {
      const value = filterValueBuilder
        ? filterValueBuilder(identity.id)
        : identity.id;
      setFilters(
        { ...filterValues, [filterKey]: value },
        displayedFilters,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, identity?.id, isAdmin]);

  const handleChange = () => {
    if (!identity?.id) return;
    const next = { ...filterValues };
    if (isActive) {
      delete next[filterKey];
    } else {
      next[filterKey] = filterValueBuilder
        ? filterValueBuilder(identity.id)
        : identity.id;
    }
    setFilters(next, displayedFilters);
  };

  return (
    <div className={"flex items-center gap-2 " + (className ?? "")}>
      <Switch
        id={`only-mine-${filterKey}`}
        checked={isActive}
        onCheckedChange={handleChange}
        disabled={isPending || !identity?.id}
      />
      <Label
        htmlFor={`only-mine-${filterKey}`}
        className="cursor-pointer text-sm"
      >
        {label}
      </Label>
    </div>
  );
}
